import React, { useCallback, useEffect, useRef, useState } from 'react';

import { setGuardrailsDialogSetFunc } from '~/components/CmsMain/globals';

import GuardrailsViolationDialog from './GuardrailsViolationDialog';
import type { GuardrailsDialogResult, GuardrailViolation } from './types';

interface PendingRequest {
  violations: GuardrailViolation[];
  isBlocked: boolean;
  resolve: (result: GuardrailsDialogResult) => void;
}

type QueuedRequest = PendingRequest & { id: number };

/**
 * Mounts once (inside `CmsMain`) and lets non-React code open the guardrails
 * warn/block dialog imperatively: it registers itself as the target of
 * `openGuardrailsDialog` (globals) and shows one dialog at a time.
 *
 * Requests are queued rather than held singly, so two guarded actions that trip
 * concurrently each get their own dialog in turn — neither promise is dropped.
 * Each queued request carries a unique `id` used as the dialog's React key, so a
 * new dialog remounts with a fresh (empty) override-reason field instead of
 * inheriting the previous one's Formik state.
 */
const GuardrailsViolationDialogHost: React.FC = () => {
  const [queue, setQueue] = useState<QueuedRequest[]>([]);
  const nextId = useRef(0);
  // Mirror of `queue` for the unmount cleanup, which closes over the mount-time state.
  const queueRef = useRef<QueuedRequest[]>([]);
  queueRef.current = queue;

  const enqueue = useCallback((request: PendingRequest) => {
    const queued: QueuedRequest = { ...request, id: nextId.current };
    nextId.current += 1;
    setQueue((q) => [...q, queued]);
  }, []);

  useEffect(() => {
    setGuardrailsDialogSetFunc(enqueue);
    return () => {
      setGuardrailsDialogSetFunc(null);
      // If the host unmounts with dialogs open/queued (e.g. logout tears down the
      // authenticated tree), resolve each as a cancel so the interceptor's awaiting
      // `openGuardrailsDialog` settles and the caller's request promise doesn't hang.
      queueRef.current.forEach((request) => request.resolve({ proceed: false, reason: '' }));
    };
  }, [enqueue]);

  const current = queue[0] ?? null;

  const settle = useCallback(
    (result: GuardrailsDialogResult) => {
      current?.resolve(result);
      setQueue((q) => q.slice(1));
    },
    [current]
  );

  if (!current) {
    return null;
  }

  return (
    <GuardrailsViolationDialog
      key={current.id}
      violations={current.violations}
      isBlocked={current.isBlocked}
      onClose={() => settle({ proceed: false, reason: '' })}
      onProceed={(reason) => settle({ proceed: true, reason })}
    />
  );
};

export default GuardrailsViolationDialogHost;
